import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { packageMeta, WHATSAPP_LINK } from "@/lib/packages";
import { galleryImages } from "@/lib/gallery";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { ArrowLeft, ArrowRight, Clock, MapPin, ShieldCheck, MessageCircle, Camera, Users, Star } from "lucide-react";
import ProductSchema, { TOUR_REVIEWS } from "@/components/ProductSchema";
import BookingForm from "@/components/BookingForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TourDetail() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const meta = packageMeta[tourId];
  //console.log(tourId);
  //console.log(meta);
  

  //console.log(`${API}/packages`);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/packages`).then((r) => {
      if (!mounted) return;
      const found = r.data.find((p) => p.id === tourId);
      if (!found) setNotFound(true);
      else setPkg(found);
    }).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
    return () => { mounted = false; };
  }, [tourId]);

  console.log(pkg);

  
  const bookNow = () => {
    navigate("/book");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("prefill-package", { detail: tourId }));
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };
  

  if (notFound || !meta) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display text-5xl text-jungle">404</h1>
          <p className="text-forest mt-4">Tour not found.</p>
          <Link to="/" className="btn-primary mt-6 inline-flex">← Back to home.</Link>
        </div>
      </div>
    );
  }

  if (!pkg) {
  //if(1!=1){
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-forest font-overline text-xs animate-pulse">Loading…</div>
      </div>
    );
  }

  const highlights = meta.highlights[lang] || meta.highlights.en;
  const longDesc = meta.long_desc[lang] || meta.long_desc.en;
  const tourImages = galleryImages.slice(0, 4);
  const reviewData = TOUR_REVIEWS[tourId] || { aggregate: null, reviews: [] };
  const detailUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div data-testid={`tour-detail-${tourId}`} className="min-h-screen bg-sand text-jungle">
      <ProductSchema pkg={pkg} image={meta.image} url={detailUrl} />
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden bg-jungle">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted loop playsInline preload="metadata" poster={meta.image}
        >
          <source src={meta.hero_video} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/40 via-jungle/30 to-jungle/85" />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-20">
          <button
            onClick={() => navigate("/")}
            data-testid="detail-back-btn"
            className="self-start mb-6 font-overline text-white text-[10px] text-sand/80 hover:text-terracotta flex items-center gap-2 fade-up"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <p className="font-overline text-white text-xs mb-4 fade-up">{pkg.location}</p>
          <h1 data-testid="detail-title" className="font-display text-white text-5xl md:text-7xl leading-[0.95] max-w-4xl fade-up-delay-1">
            {pkg.name}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sand/90 text-white fade-up-delay-2">
            <span className="flex items-center gap-2"><Clock size={16} /> {pkg.duration_min} {t.packages.duration}</span>
            <span className="flex items-center gap-2"><MapPin size={16} /> {pkg.location}</span>
            <span className="flex items-center gap-2"><Users size={16} /> 1–6 pax</span>
          </div>
        </div>
      </section>

      {/* Info + Booking */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="font-overline text-terracotta text-xs mb-3">{t.destinations.overline}</div>
            <h2 className="font-display text-3xl md:text-5xl text-jungle leading-[1.1]">About this flight</h2>
            <p className="text-forest text-base md:text-lg leading-relaxed mt-6 max-w-3xl">{longDesc}</p>

            <h3 className="font-display text-2xl text-jungle mt-12">Highlights</h3>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {highlights.map((h, i) => (
                <li
                  key={i}
                  data-testid={`detail-highlight-${i}`}
                  className="flex items-start gap-3 border border-jungle/10 p-4 bg-white"
                >
                  <ShieldCheck size={18} className="text-terracotta shrink-0 mt-0.5" />
                  <span className="text-jungle text-sm">{h}</span>
                </li>
              ))}
            </ul>

            <h3 className="font-display text-2xl text-jungle mt-12">What's included</h3>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {[
                { icon: ShieldCheck, label: t.trust.insured },
                { icon: Camera, label: "GoPro option" },
                { icon: Users, label: "Certified pilot" },
              ].map((it, i) => (
                <div key={i} className="border border-jungle/10 p-4 bg-white flex items-center gap-3">
                  <it.icon size={18} className="text-terracotta" />
                  <span className="text-jungle text-sm">{it.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky booking card */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="border border-jungle/15 bg-white p-6 md:p-8" data-testid="detail-booking-card">
              <div className="font-overline text-[10px] text-terracotta">{t.destinations.from}</div>
              <div className="font-display text-5xl text-jungle leading-none mt-1">${pkg.full_price}</div>
              <div className="text-forest text-sm mt-1">{t.destinations.per_person} · {pkg.currency?.toUpperCase()}</div>

              <div className="mt-6 pt-6 border-t border-jungle/10 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-forest">{t.book.deposit}</span>
                  <span className="font-semibold text-jungle">${pkg.deposit}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-forest">{t.book.balance}</span>
                  <span className="font-semibold text-jungle">${(pkg.full_price - pkg.deposit).toFixed(0)}</span>
                </div>
              </div>

              <button
                data-testid="detail-book-btn"
                onClick={bookNow}
                className="btn-primary w-full justify-center mt-6"
              >
                {t.packages.book_now} <ArrowRight size={16} />
              </button>
              {/*
              <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="detail-whatsapp-btn"
              className="btn-ghost w-full justify-center mt-3"
              >
                <MessageCircle size={16} /> {t.hero.cta_whatsapp}
                </a>
                */}
                
              <p className="text-forest text-xs mt-6 italic leading-relaxed">
              {t.packages.includes}
              </p>
            </div>
          </aside>
        </div>
      </section>
  
  
      


      {/* Mini gallery */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="font-overline text-terracotta text-xs mb-3">{t.gallery.overline}</div>
          <h2 className="font-display text-3xl md:text-5xl text-jungle mb-10">{t.gallery.title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {tourImages.map((img, i) => (
              <div key={i} className="aspect-square overflow-hidden group border border-jungle/10">
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/gallery" data-testid="detail-view-gallery" className="btn-ghost">See full gallery →</Link>
          </div>
        </div>
      </section>

      <BookingForm />      

      {/* Reviews (mirrors structured data) */}
      {reviewData.reviews.length > 0 && (
        <section data-testid="detail-reviews-section" className="pb-24 md:pb-32 bg-jungle text-sand">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <div className="font-overline text-terracotta text-xs mb-3">Reviews</div>
                <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">What flyers say</h2>
              </div>
              {reviewData.aggregate && (
                <div className="flex items-center gap-4" data-testid="detail-aggregate-rating">
                  <div className="flex gap-1 text-terracotta">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} size={20} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <div>
                    <div className="font-display text-2xl leading-none">{reviewData.aggregate.rating.toFixed(1)}</div>
                    <div className="font-overline text-[10px] text-sand/60 mt-1">
                      {reviewData.aggregate.count} verified {reviewData.aggregate.count === 1 ? "review" : "reviews"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviewData.reviews.map((r, i) => (
                <article
                  key={i}
                  data-testid={`detail-review-${i}`}
                  className="border border-sand/20 p-8 hover:border-terracotta transition-colors"
                >
                  <div className="flex gap-0.5 text-terracotta mb-5">
                    {Array.from({ length: r.rating }).map((_, n) => (
                      <Star key={n} size={14} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <p className="text-sand/90 text-base leading-relaxed italic">"{r.body}"</p>
                  <div className="mt-6 pt-6 border-t border-sand/10">
                    <div className="font-display text-base text-sand">{r.author}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-overline text-[10px] text-sand/60">{r.locality}, {r.country}</span>
                      <time dateTime={r.date} className="text-sand/50 text-[10px]">{r.date}</time>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
      <Footer />
    
    </div>
  );
}
